import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { ShoppingCart, ArrowLeft, Store, CreditCard, Banknote, CheckCircle } from 'lucide-react';
import axios from 'axios';
import { useB2CCart } from './PublicShopPage';
import { useI18n } from '../contexts/I18nContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PublicCheckoutPage = () => {
  const { t } = useI18n();
  const { shopSlug } = useParams();
  const navigate = useNavigate();
  const [shop, setShop] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const isSubmittingRef = useRef(false); // Track if we're in the middle of submitting
  const { items, subtotal, shipping, total, clearCart } = useB2CCart();
  
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    street: '',
    postal_code: '',
    city: '',
    country: 'Deutschland',
    note: '',
    payment_method: 'cash',
    accept_terms: false
  });

  useEffect(() => {
    // Don't redirect if we're submitting or just completed submission
    if (isSubmittingRef.current) return;
    
    if (items.length === 0) {
      navigate(`/shop/${shopSlug}`);
      return;
    }
    
    axios.get(`${API_URL}/api/public/shop/${shopSlug}`)
      .then(res => setShop(res.data))
      .catch(() => navigate('/'))
      .finally(() => setLoading(false));
  }, [shopSlug, items.length, navigate]);

  const formatPrice = (price) => {
    return new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(price);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.accept_terms) {
      toast.error(t('coAcceptTermsError'));
      return;
    }
    
    setSubmitting(true);
    isSubmittingRef.current = true; // Prevent empty cart redirect
    
    try {
      const orderData = {
        items: items.map(item => ({
          product_id: item.product_id,
          quantity: item.quantity
        })),
        first_name: formData.first_name,
        last_name: formData.last_name,
        email: formData.email,
        phone: formData.phone,
        street: formData.street,
        postal_code: formData.postal_code,
        city: formData.city,
        country: formData.country,
        note: formData.note,
        payment_method: formData.payment_method,
        origin_url: window.location.origin
      };
      
      const response = await axios.post(`${API_URL}/api/public/shop/${shopSlug}/checkout`, orderData);
      
      if (formData.payment_method === 'online' && response.data.checkout_url) {
        // Redirect to Stripe
        window.location.href = response.data.checkout_url;
      } else {
        // Cash payment - navigate FIRST, then clear cart
        const successUrl = `/shop/${shopSlug}/order-success?order_id=${response.data.order_id}&order_number=${response.data.order_number}`;
        navigate(successUrl);
        // Clear cart after navigation is initiated
        setTimeout(() => clearCart(), 100);
      }
    } catch (error) {
      isSubmittingRef.current = false;
      toast.error(error.response?.data?.detail || 'Fehler bei der Bestellung');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <div className="flex items-center gap-3">
            {shop?.logo_url ? (
              <img 
                src={`${API_URL}/api/files/${shop.logo_url}`} 
                alt={shop.shop_name} 
                className="h-10 w-10 object-contain"
              />
            ) : (
              <Store className="h-8 w-8 text-primary" />
            )}
            <h1 className="text-xl font-bold">{shop?.shop_name} - {t('coCheckoutTitle')}</h1>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-6">
        <Button variant="ghost" onClick={() => navigate(`/shop/${shopSlug}/cart`)} className="mb-4">
          <ArrowLeft className="h-4 w-4 me-1" />
          {t('coBackToCart')}
        </Button>

        <form onSubmit={handleSubmit}>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Form */}
            <div className="lg:col-span-2 space-y-6">
              {/* Contact */}
              <div className="bg-white border border-border rounded-lg p-6">
                <h2 className="text-lg font-bold mb-4">{t('coContactData')}</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="first_name">{t('firstName')} *</Label>
                    <Input
                      id="first_name"
                      value={formData.first_name}
                      onChange={(e) => setFormData({...formData, first_name: e.target.value})}
                      required
                      className="mt-1"
                      data-testid="first-name-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="last_name">{t('lastName')} *</Label>
                    <Input
                      id="last_name"
                      value={formData.last_name}
                      onChange={(e) => setFormData({...formData, last_name: e.target.value})}
                      required
                      className="mt-1"
                      data-testid="last-name-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="email">E-Mail *</Label>
                    <Input
                      id="email"
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({...formData, email: e.target.value})}
                      required
                      className="mt-1"
                      data-testid="email-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="phone">{t('phone')}</Label>
                    <Input
                      id="phone"
                      value={formData.phone}
                      onChange={(e) => setFormData({...formData, phone: e.target.value})}
                      className="mt-1"
                      data-testid="phone-input"
                    />
                  </div>
                </div>
              </div>

              {/* Shipping */}
              <div className="bg-white border border-border rounded-lg p-6">
                <h2 className="text-lg font-bold mb-4">{t('deliveryAddress')}</h2>
                <div className="grid grid-cols-1 gap-4">
                  <div>
                    <Label htmlFor="street">{t('coStreet')}</Label>
                    <Input
                      id="street"
                      value={formData.street}
                      onChange={(e) => setFormData({...formData, street: e.target.value})}
                      required
                      className="mt-1"
                      data-testid="street-input"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="postal_code">{t('zip')} *</Label>
                      <Input
                        id="postal_code"
                        value={formData.postal_code}
                        onChange={(e) => setFormData({...formData, postal_code: e.target.value})}
                        required
                        className="mt-1"
                        data-testid="postal-code-input"
                      />
                    </div>
                    <div>
                      <Label htmlFor="city">{t('city')} *</Label>
                      <Input
                        id="city"
                        value={formData.city}
                        onChange={(e) => setFormData({...formData, city: e.target.value})}
                        required
                        className="mt-1"
                        data-testid="city-input"
                      />
                    </div>
                  </div>
                  <div>
                    <Label htmlFor="country">{t('country')}</Label>
                    <Input
                      id="country"
                      value={formData.country}
                      onChange={(e) => setFormData({...formData, country: e.target.value})}
                      className="mt-1"
                    />
                  </div>
                </div>
              </div>

              {/* Payment */}
              <div className="bg-white border border-border rounded-lg p-6">
                <h2 className="text-lg font-bold mb-4">{t('paymentMethod')}</h2>
                <div className="space-y-3">
                  <label className={`flex items-center gap-3 p-4 border rounded-lg cursor-pointer ${formData.payment_method === 'cash' ? 'border-primary bg-primary/5' : 'hover:bg-gray-50'}`}>
                    <input
                      type="radio"
                      name="payment_method"
                      value="cash"
                      checked={formData.payment_method === 'cash'}
                      onChange={(e) => setFormData({...formData, payment_method: e.target.value})}
                      className="w-4 h-4"
                      data-testid="payment-cash"
                    />
                    <Banknote className="h-5 w-5 text-gray-600" />
                    <div>
                      <div className="font-medium">{t('coCashOnDelivery')}</div>
                      <div className="text-sm text-gray-500">{t('coPayOnDelivery')}</div>
                    </div>
                  </label>
                  <label className={`flex items-center gap-3 p-4 border rounded-lg cursor-pointer ${formData.payment_method === 'online' ? 'border-primary bg-primary/5' : 'hover:bg-gray-50'}`}>
                    <input
                      type="radio"
                      name="payment_method"
                      value="online"
                      checked={formData.payment_method === 'online'}
                      onChange={(e) => setFormData({...formData, payment_method: e.target.value})}
                      className="w-4 h-4"
                      data-testid="payment-online"
                    />
                    <CreditCard className="h-5 w-5 text-gray-600" />
                    <div>
                      <div className="font-medium">{t('coOnlinePayment')}</div>
                      <div className="text-sm text-gray-500">{t('coCardKlarna')}</div>
                    </div>
                  </label>
                </div>
              </div>

              {/* Note */}
              <div className="bg-white border border-border rounded-lg p-6">
                <h2 className="text-lg font-bold mb-4">{t('coOrderNote')}</h2>
                <textarea
                  value={formData.note}
                  onChange={(e) => setFormData({...formData, note: e.target.value})}
                  placeholder={t('coNotePlaceholder')}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                  rows={3}
                  data-testid="note-input"
                />
              </div>

              {/* Terms */}
              <div className="bg-white border border-border rounded-lg p-6">
                <label className="flex items-start gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.accept_terms}
                    onChange={(e) => setFormData({...formData, accept_terms: e.target.checked})}
                    className="w-5 h-5 mt-0.5 rounded border-gray-300"
                    data-testid="accept-terms"
                  />
                  <span className="text-sm">
                    {t('coIAccept')} <a href="/agb" target="_blank" rel="noopener noreferrer" className="text-primary underline">AGB</a> {t('coAnd')} 
                    <a href="/datenschutz" target="_blank" rel="noopener noreferrer" className="text-primary underline"> {t('privacyPolicy')}</a> *
                  </span>
                </label>
              </div>
            </div>

            {/* Summary */}
            <div className="bg-white border border-border rounded-lg p-6 h-fit sticky top-4">
              <h2 className="text-lg font-bold mb-4">{t('orderSummary')}</h2>
              
              <div className="space-y-3 mb-4">
                {items.map(item => (
                  <div key={item.product_id} className="flex justify-between text-sm">
                    <span>{item.quantity}x {item.name}</span>
                    <span>{formatPrice(item.price * item.quantity)}</span>
                  </div>
                ))}
              </div>
              
              <hr className="my-4" />
              
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span>{t('subtotal')}</span>
                  <span>{formatPrice(subtotal)}</span>
                </div>
                <div className="flex justify-between">
                  <span>{t('shipping')}</span>
                  <span>{shipping === 0 ? 'Kostenlos' : formatPrice(shipping)}</span>
                </div>
                <hr className="my-3" />
                <div className="flex justify-between font-bold text-lg">
                  <span>{t('total')}</span>
                  <span>{formatPrice(total)}</span>
                </div>
                <p className="text-xs text-gray-500">{t('psInclVat')}</p>
              </div>
              
              <Button 
                type="submit"
                className="w-full mt-6" 
                size="lg"
                disabled={submitting}
                data-testid="place-order-btn"
              >
                {submitting ? t('coProcessing') : `${t('coOrderNow')} - ${formatPrice(total)}`}
              </Button>
            </div>
          </div>
        </form>
      </main>
    </div>
  );
};

// Order Success Page
export const PublicOrderSuccessPage = () => {
  const { t } = useI18n();
  const { shopSlug } = useParams();
  const navigate = useNavigate();
  const [shop, setShop] = useState(null);
  const searchParams = new URLSearchParams(window.location.search);
  const orderId = searchParams.get('order_id');
  const orderNumber = searchParams.get('order_number');

  useEffect(() => {
    axios.get(`${API_URL}/api/public/shop/${shopSlug}`)
      .then(res => setShop(res.data))
      .catch(() => {});
  }, [shopSlug]);

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
      <div className="bg-white border border-border rounded-lg p-8 max-w-md w-full text-center">
        <CheckCircle className="h-16 w-16 text-green-500 mx-auto mb-4" />
        <h1 className="text-2xl font-bold mb-2">{t('coThankYou')}</h1>
        <p className="text-gray-600 mb-4">
          {t('coOrderPlaced')}
        </p>
        {orderNumber && (
          <div className="bg-gray-50 rounded-lg p-4 mb-6">
            <p className="text-sm text-gray-500">{t('orderNumber')}</p>
            <p className="text-xl font-bold text-primary">{orderNumber}</p>
          </div>
        )}
        <p className="text-sm text-gray-500 mb-6">
          {t('coSuccessInfo')}{' '}
          {t('coQuestionsContact', { name: shop?.shop_name || 'Shop' })}
        </p>
        <Button onClick={() => navigate(`/shop/${shopSlug}`)} className="w-full">
          {t('continueShopping')}
        </Button>
      </div>
    </div>
  );
};

export default PublicCheckoutPage;
